/***********************************************************************
 Utility class for SCORM API detection
 ***********************************************************************/
import {ScormType} from "scorm/common/ScormType";
import {LoggingService} from "logging/LoggingService";
import {Assert} from "common/Assert";

export function ScormApiDetector() {

    function getLookupVersion( lookupTarget ){
        switch( lookupTarget ) {
            case "1.3":
            case "1_3":
            case "2004":
            case "scorm2004":
                return ScormType.SCORM_1_3;
            default:
                return ScormType.SCORM_1_2;
        }
    }

    /**
     * @param version
     * @returns {*}     null if objRootWin hasn't a parent.
     */
    function getScormAPI( version ) {
        try {
            let rootWin = window;

            if (!isParentAccessible(rootWin)) {
                return null;
            }

            let scormAPI = getScormAPIRecursive(rootWin, version);
            if (!!scormAPI) {
                return scormAPI;
            }

            // in case no API is found, search in parent.opener:
            if (!isOpenerAccessible(rootWin.parent)) {
                return null;
            }

            return getScormAPIRecursive(rootWin.parent.opener, version);
        }
        catch(e) {
            LoggingService.error("Error in getScormAPI", e);
        }
        return null;
    }

    function isParentAccessible(someWin) {
        try {
            const winParent = someWin.parent;
            return (typeof(winParent) !== "undefined") && (winParent !== null);
        }
        catch (e) {
            return false;
        }
    }

    function isOpenerAccessible( someWin ) {
        try {
            const opener = someWin.opener;
            return typeof opener !== "undefined" && !!opener;
        }
        catch (e) {
            return false;
        }
    }

    function getScormAPIRecursive( win, version ) {
        let scormAPI = findScormAPI(win, version);
        if (!!scormAPI) {
            return scormAPI;
        }

        if (!isParentAccessible(win)) {
            return null;
        }

        const myParent = win.parent;
        if (typeof myParent === "undefined" || myParent === null || myParent === win) {
            return null;
        }
        try {
            return getScormAPIRecursive(myParent, version);
        }
        catch(e) {
            LoggingService.error("Error in getScormAPIRecursive", e);
        }

        return null;
    }

    function findScormAPI( win, scormType )	{
        try {
            return lookupWindowForScormApi( win, scormType ) || lookupFramesetForScormApi( win, scormType );
        }
        catch (e) {
            LoggingService.error("Error in findScormAPI", e);
        }
        return null;
    }

    function lookupWindowForScormApi( win, scormType ){
        if( scormType === ScormType.SCORM_1_3 ) {
            if (!!win.API_1484_11) {
                return win.API_1484_11;
            }
        }
        else {
            if( !!win.API ) {
                return win.API;
            }
        }

        return null;
    }

    function lookupFramesetForScormApi( win, scormType ){
        if (win.length === 0) {
            return null;
        }

        let scormAPI = null;
        // if frames available..
        for (let i=0; i<win.length; i++) {
            try {
                scormAPI = findScormAPI( win.frames[i], scormType );
            }
            catch(e) {
                LoggingService.error("Error in findScormAPI", e);
                continue;
            }

            if (scormAPI) {
                return scormAPI;
            }
        }

        return null;
    }

    // revealing public api
    return {

        /**
         * @param lookupTarget
         * @param rootWin   if !rootWin then parent will be used.
         * @returns {*}
         */
        lookupScormApi: function (lookupTarget) {
            Assert.checkState( !lookupTarget || typeof  lookupTarget === "string" );

            if ( lookupTarget && ( lookupTarget !== "auto")) {
                const lookupVersion = getLookupVersion( lookupTarget );
                return getScormAPI(lookupVersion);
            }

            let foundAPI = getScormAPI(ScormType.SCORM_1_3);
            if (!!foundAPI) {
                return foundAPI;
            }

            return getScormAPI(ScormType.SCORM_1_2);
        }
    };
}
